import { FC, useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  Input,
  Textarea,
  Portal,
} from "@chakra-ui/react";

interface GuestbookEntry {
  id: number;
  name: string;
  message: string;
  date: string;
  country?: string;
}

interface GuestbookProps {
  isOpen: boolean;
  onClose: () => void;
}

// Default entries to make it look lively
const defaultEntries: GuestbookEntry[] = [
  {
    id: 1,
    name: "JulienDu90",
    message: "Awesome website! Love the retro vibes! 🔥",
    date: "1999-03-15",
    country: "FR",
  },
  {
    id: 2,
    name: "3ezdinLafette",
    message: "Great portfolio! Keep up the good work!",
    date: "1999-05-22",
    country: "TN",
  },
  {
    id: 3,
    name: "HansFlemmenwerfer",
    message: "Nice HTML skills! Check out my site too!",
    date: "1999-08-10",
    country: "🇩🇪",
  },
  {
    id: 4,
    name: "<UwU><EmoGirl></UwU>",
    message: "This is so nostalgic! Reminds me of GeoCities!",
    date: "2000-01-01",
    country: "🇨🇦",
  },
];

export const Guestbook: FC<GuestbookProps> = ({ isOpen, onClose }) => {
  const [entries, setEntries] = useState<GuestbookEntry[]>([]);
  const [newName, setNewName] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  // Load entries from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("retro_guestbook");
    if (stored) {
      setEntries(JSON.parse(stored));
    } else {
      setEntries(defaultEntries);
      localStorage.setItem("retro_guestbook", JSON.stringify(defaultEntries));
    }
  }, []);

  const handleSubmit = () => {
    if (!newName.trim() || !newMessage.trim()) {
      alert("Please fill in both your name and message!");
      return;
    }

    const newEntry: GuestbookEntry = {
      id: Date.now(),
      name: newName,
      message: newMessage,
      date: new Date().toISOString().split("T")[0],
      country: "🌍",
    };

    const updatedEntries = [newEntry, ...entries];
    setEntries(updatedEntries);
    localStorage.setItem("retro_guestbook", JSON.stringify(updatedEntries));

    setNewName("");
    setNewMessage("");
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleClear = () => {
    if (
      // eslint-disable-next-line no-restricted-globals
      confirm(
        "Are you sure you want to clear all entries? This cannot be undone!",
      )
    ) {
      setEntries(defaultEntries);
      localStorage.setItem("retro_guestbook", JSON.stringify(defaultEntries));
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      {/* Backdrop */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0,0,0,0.7)"
        zIndex={2000}
        display="flex"
        alignItems="center"
        justifyContent="center"
        onClick={onClose}
      >
        {/* Guestbook Window */}
        <Box
          bg="#C0C0C0"
          border="3px outset white"
          boxShadow="8px 8px 0px #404040"
          maxW="600px"
          maxH="80vh"
          w="90%"
          onClick={(e) => e.stopPropagation()}
          animation="popIn 0.3s ease-out"
          sx={{
            "@keyframes popIn": {
              from: { transform: "scale(0.8)", opacity: 0 },
              to: { transform: "scale(1)", opacity: 1 },
            },
          }}
        >
          {/* Title Bar */}
          <Flex
            bg="linear-gradient(90deg, #800080, #FF00FF)"
            px={3}
            py={2}
            justify="space-between"
            align="center"
            borderBottom="2px solid #808080"
          >
            <Text
              fontFamily="'Comic Sans MS', cursive"
              fontWeight="bold"
              color="white"
            >
              📝 Sign My Guestbook!
            </Text>
            <Box
              w="20px"
              h="20px"
              bg="#C0C0C0"
              border="2px outset white"
              textAlign="center"
              lineHeight="16px"
              fontSize="12px"
              cursor="pointer"
              onClick={onClose}
              _hover={{ bg: "#FF0000", color: "white" }}
            >
              ✕
            </Box>
          </Flex>

          <Box p={4} maxH="calc(80vh - 50px)" overflowY="auto">
            {/* Success Message */}
            {showSuccess && (
              <Box
                bg="#00FF00"
                border="2px solid #008000"
                p={2}
                mb={3}
                textAlign="center"
                animation="flash 0.5s"
                sx={{
                  "@keyframes flash": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.5 },
                  },
                }}
              >
                <Text
                  fontFamily="'Comic Sans MS', cursive"
                  fontWeight="bold"
                  color="black"
                >
                  ✨ Thank you for signing my guestbook! ✨
                </Text>
              </Box>
            )}

            {/* Sign Form */}
            <Box bg="#FFFFCC" border="3px ridge #808080" p={3} mb={4}>
              <Text
                fontFamily="'Comic Sans MS', cursive"
                fontWeight="bold"
                color="#FF0000"
                mb={2}
              >
                ✏️ Leave a message:
              </Text>
              <VStack spacing={2} align="stretch">
                <Flex align="center" gap={2}>
                  <Text
                    fontFamily="'Comic Sans MS', cursive"
                    color="black"
                    minW="80px"
                  >
                    Name:
                  </Text>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Your cool nickname..."
                    bg="white"
                    border="2px inset #808080"
                    borderRadius="0"
                    fontFamily="'Comic Sans MS', cursive"
                    color="black"
                    _placeholder={{ color: "gray.500" }}
                  />
                </Flex>
                <Flex align="start" gap={2}>
                  <Text
                    fontFamily="'Comic Sans MS', cursive"
                    color="black"
                    minW="80px"
                    pt={2}
                  >
                    Message:
                  </Text>
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Write something nice! 😊"
                    bg="white"
                    border="2px inset #808080"
                    borderRadius="0"
                    fontFamily="'Comic Sans MS', cursive"
                    color="black"
                    rows={3}
                    _placeholder={{ color: "gray.500" }}
                  />
                </Flex>
                <Flex justify="center" gap={2} mt={2}>
                  <Box
                    as="button"
                    bg="#00FF00"
                    color="black"
                    border="3px outset #66FF66"
                    px={4}
                    py={2}
                    fontFamily="'Comic Sans MS', cursive"
                    fontWeight="bold"
                    cursor="pointer"
                    _hover={{ bg: "#00CC00" }}
                    _active={{ border: "3px inset #00FF00" }}
                    onClick={handleSubmit}
                  >
                    ✅ Sign Guestbook!
                  </Box>
                  <Box
                    as="button"
                    bg="#C0C0C0"
                    color="black"
                    border="3px outset white"
                    px={4}
                    py={2}
                    fontFamily="'Comic Sans MS', cursive"
                    fontWeight="bold"
                    cursor="pointer"
                    _hover={{ bg: "#A0A0A0" }}
                    onClick={() => {
                      setNewName("");
                      setNewMessage("");
                    }}
                  >
                    🔄 Clear
                  </Box>
                </Flex>
              </VStack>
            </Box>

            {/* Rainbow Divider */}
            <Box
              h="6px"
              bg="linear-gradient(90deg, #FF0000, #FF7F00, #FFFF00, #00FF00, #0000FF, #4B0082, #8F00FF)"
              bgSize="200% 100%"
              animation="rainbow 2s linear infinite"
              mb={4}
              sx={{
                "@keyframes rainbow": {
                  from: { backgroundPosition: "0% 50%" },
                  to: { backgroundPosition: "200% 50%" },
                },
              }}
            />

            {/* Entries Header */}
            <Flex justify="space-between" align="center" mb={3}>
              <Text
                fontFamily="'Comic Sans MS', cursive"
                fontWeight="bold"
                color="black"
              >
                📖 Previous Visitors ({entries.length}):
              </Text>
              <Box
                as="button"
                bg="#FF6600"
                color="white"
                border="2px outset #FF8833"
                px={2}
                py={1}
                fontSize="xs"
                fontFamily="'Comic Sans MS', cursive"
                cursor="pointer"
                _hover={{ bg: "#CC5200" }}
                onClick={handleClear}
              >
                🗑️ Reset
              </Box>
            </Flex>

            {/* Guestbook Entries */}
            <VStack spacing={3} align="stretch">
              {entries.map((entry) => (
                <Box
                  key={entry.id}
                  bg="#000080"
                  border="2px ridge #00FFFF"
                  p={3}
                >
                  <Flex justify="space-between" mb={1}>
                    <Text
                      fontFamily="'Comic Sans MS', cursive"
                      fontWeight="bold"
                      color="#00FFFF"
                    >
                      {entry.country} {entry.name}
                    </Text>
                    <Text
                      fontFamily="'VT323', monospace"
                      fontSize="sm"
                      color="#808080"
                    >
                      {entry.date}
                    </Text>
                  </Flex>
                  <Text
                    fontFamily="'Comic Sans MS', cursive"
                    color="white"
                    fontSize="sm"
                  >
                    {entry.message}
                  </Text>
                </Box>
              ))}
            </VStack>

            {/* Footer */}
            <Box textAlign="center" mt={4}>
              <Text
                fontFamily="'Comic Sans MS', cursive"
                fontSize="xs"
                color="#808080"
              >
                🌐 Powered by Retro Guestbook 2000™ 🌐
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </Portal>
  );
};
